import { api } from '../services/api';
import React, { useState, useEffect } from 'react';

const AddMatch = ({ onSubmit, loading, token }) => {
  const [matchDate, setMatchDate] = useState('');
  const [availablePlayers, setAvailablePlayers] = useState([]);
  const [teamAPlayers, setTeamAPlayers] = useState([]);
  const [teamBPlayers, setTeamBPlayers] = useState([]);
  const [teamAScore, setTeamAScore] = useState({ firstHalf: 0, secondHalf: 0 });
  const [teamBScore, setTeamBScore] = useState({ firstHalf: 0, secondHalf: 0 });
  const [currentTeam, setCurrentTeam] = useState('A');

  // Load available players
  useEffect(() => {
    const loadPlayers = async () => {
      try {
        const data = await api.getPlayers(token);
        setAvailablePlayers(data);
      } catch (error) {
        console.error('Error loading players:', error);
      }
    };
    loadPlayers();
  }, [token]);

  const addPlayerToTeam = (team) => {
    const newPlayer = {
      playerId: '', name: '', position: '',
      goals: 0, saves: 0, assists: 0,
      shots_faced: 0, goals_conceded: 0,
      penalties_faced: 0, penalties_saved: 0,
      yellow_cards: 0, red_cards: 0
    };

    if (team === 'A') {
      setTeamAPlayers([...teamAPlayers, newPlayer]);
    } else {
      setTeamBPlayers([...teamBPlayers, newPlayer]);
    }
  };

  const removePlayer = (team, index) => {
    if (team === 'A') {
      setTeamAPlayers(teamAPlayers.filter((_, i) => i !== index));
    } else {
      setTeamBPlayers(teamBPlayers.filter((_, i) => i !== index));
    }
  };

  const updatePlayer = (team, index, field, value) => {
    const updateTeam = team === 'A' ? teamAPlayers : teamBPlayers;
    const updated = [...updateTeam];

    if (field === 'playerId') {
      const player = availablePlayers.find(p => p.id === parseInt(value));
      if (player) {
        updated[index] = {
          ...updated[index],
          playerId: player.id,
          name: player.name,
          position: player.position || 'Forward'
        };
      }
    } else {
      updated[index][field] = field === 'name' || field === 'position' ? value : parseInt(value) || 0;
    }

    if (team === 'A') {
      setTeamAPlayers(updated);
    } else {
      setTeamBPlayers(updated);
    }
  };

  const handleSubmit = () => {
    if (!matchDate) {
      alert('Please select a match date');
      return;
    }

    if (teamAPlayers.length === 0 && teamBPlayers.length === 0) {
      alert('Please add at least one player');
      return;
    }

    const allPlayers = [...teamAPlayers, ...teamBPlayers]
      .filter(p => p.name.trim() !== '')
      .map(p => ({
        name: p.name, goals: p.goals, saves: p.saves, assists: p.assists,
        shots_faced: p.shots_faced, goals_conceded: p.goals_conceded,
        penalties_faced: p.penalties_faced, penalties_saved: p.penalties_saved,
        yellow_cards: p.yellow_cards, red_cards: p.red_cards
      }));

    const matchData = {
      date: matchDate,
      players: allPlayers,
      teamA: {
        players: teamAPlayers.filter(p => p.name.trim() !== '').map(p => p.name),
        score: {
          firstHalf: teamAScore.firstHalf,
          secondHalf: teamAScore.secondHalf,
          total: teamAScore.firstHalf + teamAScore.secondHalf
        }
      },
      teamB: {
        players: teamBPlayers.filter(p => p.name.trim() !== '').map(p => p.name),
        score: {
          firstHalf: teamBScore.firstHalf,
          secondHalf: teamBScore.secondHalf,
          total: teamBScore.firstHalf + teamBScore.secondHalf
        }
      }
    };

    onSubmit(matchData);
  };

  const StatSelector = ({ label, value, onChange, max = 10 }) => (
    <div>
      <label className="block text-stone-700 font-manrope text-[10px] uppercase tracking-widest font-bold mb-1.5 pl-1">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(parseInt(e.target.value))}
        className="w-full bg-background border border-outline-variant/50 rounded-xl px-4 py-3 text-on-background font-manrope focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all cursor-pointer"
      >
        {[...Array(max + 1)].map((_, i) => (
          <option key={i} value={i}>{i}</option>
        ))}
      </select>
    </div>
  );

  const PlayerCard = ({ player, index, team }) => {
    const isTeamA = team === 'A';
    const accentColor = isTeamA ? 'text-primary' : 'text-blue-700';
    const bgAccent = isTeamA ? 'bg-primary/10 border-primary/20' : 'bg-blue-500/10 border-blue-500/20';

    return (
      <div className="bg-surface-container-high rounded-3xl p-5 sm:p-6 border border-outline-variant/30 shadow-lg relative group transition-all hover:border-outline-variant">
        
        {/* Top Header Row */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-6">
          <div className={`w-14 h-14 rounded-2xl flex flex-shrink-0 items-center justify-center border ${bgAccent} ${accentColor}`}>
            <span className="material-symbols-outlined text-2xl" style={{fontVariationSettings: "'FILL' 1"}}>person</span>
          </div>
          
          <div className="flex-1 w-full">
            <select
              value={player.playerId || ''}
              onChange={(e) => updatePlayer(team, index, 'playerId', e.target.value)}
              className="w-full bg-background border border-outline-variant/50 rounded-xl px-4 py-3.5 text-on-background font-lexend font-bold focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all cursor-pointer"
            >
              <option value="">Assign Player...</option>
              {availablePlayers.map(p => (
                <option key={p.id} value={p.id}>{p.name} — {p.position}</option>
              ))}
            </select>
          </div>

          <button
            onClick={() => removePlayer(team, index)}
            className="self-end sm:self-auto p-3.5 text-stone-700 hover:text-error hover:bg-error/10 rounded-xl transition-colors border border-transparent hover:border-error/20"
            title="Remove Player"
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        {/* Player Stats Form */}
        {player.name && (
          <div className="space-y-4 animate-fadeIn">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-background border border-outline-variant/30 rounded-lg">
              <span className="material-symbols-outlined text-stone-700 text-[16px]">radar</span>
              <span className="text-stone-600 font-manrope text-[10px] font-bold uppercase tracking-widest">Role:</span>
              <span className="text-on-background font-lexend font-bold text-sm">{player.position}</span>
            </div>

            <div className="grid grid-cols-3 gap-3 sm:gap-4 p-4 bg-background rounded-2xl border border-outline-variant/30">
              <StatSelector label="Goals" value={player.goals} onChange={(val) => updatePlayer(team, index, 'goals', val)} />
              <StatSelector label="Assists" value={player.assists} onChange={(val) => updatePlayer(team, index, 'assists', val)} />
              <StatSelector label="Saves" value={player.saves} onChange={(val) => updatePlayer(team, index, 'saves', val)} />
            </div>

            {player.position === 'Goalkeeper' && (
              <details className="group/gk bg-background border border-outline-variant/30 rounded-2xl overflow-hidden mt-4">
                <summary className="flex items-center justify-between p-4 cursor-pointer hover:bg-surface-container-highest transition-colors">
                  <div className="flex items-center gap-2">
                    <span className="material-symbols-outlined text-purple-700">shield</span>
                    <span className="text-on-background font-lexend font-bold text-sm">GK Analytics Matrix</span>
                  </div>
                  <span className="material-symbols-outlined text-stone-700 group-open/gk:rotate-180 transition-transform">expand_more</span>
                </summary>
                <div className="p-4 border-t border-outline-variant/30 grid grid-cols-2 sm:grid-cols-4 gap-3 bg-surface-container/50">
                  <StatSelector label="Shots Faced" value={player.shots_faced} max={30} onChange={(val) => updatePlayer(team, index, 'shots_faced', val)} />
                  <StatSelector label="Conceded" value={player.goals_conceded} onChange={(val) => updatePlayer(team, index, 'goals_conceded', val)} />
                  <StatSelector label="Pens Faced" value={player.penalties_faced} onChange={(val) => updatePlayer(team, index, 'penalties_faced', val)} />
                  <StatSelector label="Pens Saved" value={player.penalties_saved} onChange={(val) => updatePlayer(team, index, 'penalties_saved', val)} />
                </div>
              </details>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6 sm:space-y-8 animate-fadeIn pb-32">
      
      {/* 1. Date Configuration */}
      <div className="bg-surface-container border border-outline-variant/20 rounded-3xl p-6 sm:p-8 shadow-lg">
        <h2 className="text-on-background text-2xl font-lexend font-bold mb-1">Fixture Configuration</h2>
        <p className="text-stone-600 text-sm font-manrope mb-6">Initialize match parameters and timestamp.</p>
        
        <div className="relative group max-w-md">
          <span className="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-stone-700 group-focus-within:text-primary transition-colors">calendar_month</span>
          <input
            type="datetime-local"
            value={matchDate}
            onChange={(e) => setMatchDate(e.target.value)}
            className="w-full bg-background border border-outline-variant/50 rounded-xl pl-12 pr-4 py-4 text-on-background font-lexend font-bold placeholder:text-stone-800 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all shadow-inner"
          />
        </div>
      </div>

      {/* 2. Team Tab Navigation */}
      <div className="flex gap-3 bg-surface-container p-2 rounded-2xl border border-outline-variant/20">
        <button
          onClick={() => setCurrentTeam('A')}
          className={`flex-1 py-4 rounded-xl font-lexend font-bold tracking-wide transition-all duration-300 ${
            currentTeam === 'A'
              ? 'bg-primary text-on-primary shadow-lg shadow-primary/20'
              : 'text-stone-600 hover:text-on-background hover:bg-surface-container-high'
          }`}
        >
          Squad Alpha ({teamAPlayers.length})
        </button>
        <button
          onClick={() => setCurrentTeam('B')}
          className={`flex-1 py-4 rounded-xl font-lexend font-bold tracking-wide transition-all duration-300 ${
            currentTeam === 'B'
              ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/20'
              : 'text-stone-600 hover:text-on-background hover:bg-surface-container-high'
          }`}
        >
          Squad Bravo ({teamBPlayers.length})
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
        
        {/* 3. Main Roster Area (2/3 width on desktop) */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-surface-container border border-outline-variant/20 rounded-3xl p-6 sm:p-8 shadow-lg">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
              <div>
                <h3 className="text-on-background text-xl font-lexend font-bold flex items-center gap-2">
                  <span className={`w-3 h-3 rounded-full ${currentTeam === 'A' ? 'bg-primary' : 'bg-blue-500'}`}></span>
                  {currentTeam === 'A' ? 'Squad Alpha' : 'Squad Bravo'} Roster
                </h3>
                <p className="text-stone-600 text-sm font-manrope mt-1">Assign players and log statistics.</p>
              </div>
              <button
                onClick={() => addPlayerToTeam(currentTeam)}
                className="bg-background border border-outline-variant/50 hover:bg-surface-container-high hover:border-outline-variant text-on-background px-5 py-3 rounded-xl font-lexend font-bold text-sm transition-all active:scale-95 flex items-center justify-center gap-2 shadow-sm"
              >
                <span className="material-symbols-outlined text-[20px]">person_add</span>
                Draft Player
              </button>
            </div>

            <div className="space-y-4">
              {(currentTeam === 'A' ? teamAPlayers : teamBPlayers).map((player, index) => (
                <PlayerCard key={index} player={player} index={index} team={currentTeam} />
              ))}
            </div>

            {((currentTeam === 'A' && teamAPlayers.length === 0) || 
              (currentTeam === 'B' && teamBPlayers.length === 0)) && (
              <div className="text-center py-16 px-4 border-2 border-dashed border-outline-variant/30 rounded-3xl mt-4">
                <span className="material-symbols-outlined text-6xl text-stone-900 mb-4">group_off</span>
                <p className="text-on-background font-lexend font-bold text-lg mb-1">Roster Empty</p>
                <p className="text-stone-700 font-manrope text-sm">Add players to begin tracking performance.</p>
              </div>
            )}
          </div>
        </div>

        {/* 4. Score Entry & Summary (1/3 width on desktop) */}
        <div className="space-y-6 sm:space-y-8">
          
          {/* Live Score Entry */}
          <div className="bg-surface-container border border-outline-variant/20 rounded-3xl p-6 sm:p-8 shadow-lg">
            <div className="mb-6">
              <h3 className="text-on-background text-xl font-lexend font-bold flex items-center gap-2">
                <span className="material-symbols-outlined text-emerald-700">scoreboard</span>
                Match Score
              </h3>
            </div>
            
            <div className="space-y-6">
              <div className="bg-background rounded-2xl p-4 border border-outline-variant/30">
                <p className="text-stone-600 font-manrope text-[10px] font-bold uppercase tracking-widest text-center mb-3">1st Half</p>
                <div className="flex gap-4">
                  <div className="flex-1">
                    <p className="text-center text-primary font-lexend font-bold text-xs mb-2">Alpha</p>
                    <select
                      value={teamAScore.firstHalf}
                      onChange={(e) => setTeamAScore({...teamAScore, firstHalf: parseInt(e.target.value)})}
                      className="w-full bg-surface-container-high border border-outline-variant/50 rounded-xl px-2 py-3 text-center text-on-background font-lexend font-bold text-xl focus:border-primary focus:ring-1 focus:ring-primary appearance-none cursor-pointer"
                    >
                      {[...Array(21)].map((_, i) => <option key={i} value={i}>{i}</option>)}
                    </select>
                  </div>
                  <div className="flex flex-col justify-center"><span className="text-stone-800 font-bold">:</span></div>
                  <div className="flex-1">
                    <p className="text-center text-blue-700 font-lexend font-bold text-xs mb-2">Bravo</p>
                    <select
                      value={teamBScore.firstHalf}
                      onChange={(e) => setTeamBScore({...teamBScore, firstHalf: parseInt(e.target.value)})}
                      className="w-full bg-surface-container-high border border-outline-variant/50 rounded-xl px-2 py-3 text-center text-on-background font-lexend font-bold text-xl focus:border-blue-500 focus:ring-1 focus:ring-blue-500 appearance-none cursor-pointer"
                    >
                      {[...Array(21)].map((_, i) => <option key={i} value={i}>{i}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              <div className="bg-background rounded-2xl p-4 border border-outline-variant/30">
                <p className="text-stone-600 font-manrope text-[10px] font-bold uppercase tracking-widest text-center mb-3">2nd Half</p>
                <div className="flex gap-4">
                  <div className="flex-1">
                    <select
                      value={teamAScore.secondHalf}
                      onChange={(e) => setTeamAScore({...teamAScore, secondHalf: parseInt(e.target.value)})}
                      className="w-full bg-surface-container-high border border-outline-variant/50 rounded-xl px-2 py-3 text-center text-on-background font-lexend font-bold text-xl focus:border-primary focus:ring-1 focus:ring-primary appearance-none cursor-pointer"
                    >
                      {[...Array(21)].map((_, i) => <option key={i} value={i}>{i}</option>)}
                    </select>
                  </div>
                  <div className="flex flex-col justify-center"><span className="text-stone-800 font-bold">:</span></div>
                  <div className="flex-1">
                    <select
                      value={teamBScore.secondHalf}
                      onChange={(e) => setTeamBScore({...teamBScore, secondHalf: parseInt(e.target.value)})}
                      className="w-full bg-surface-container-high border border-outline-variant/50 rounded-xl px-2 py-3 text-center text-on-background font-lexend font-bold text-xl focus:border-blue-500 focus:ring-1 focus:ring-blue-500 appearance-none cursor-pointer"
                    >
                      {[...Array(21)].map((_, i) => <option key={i} value={i}>{i}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              <div className="pt-6 border-t border-outline-variant/20 flex items-end justify-center gap-6">
                <div className="text-center">
                  <p className="text-primary font-lexend font-black text-5xl">{teamAScore.firstHalf + teamAScore.secondHalf}</p>
                </div>
                <div className="pb-2 text-stone-800 font-black text-2xl">-</div>
                <div className="text-center">
                  <p className="text-blue-700 font-lexend font-black text-5xl">{teamBScore.firstHalf + teamBScore.secondHalf}</p>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Floating Action Bar (Fixed to bottom for easy submission) */}
      <div className="fixed bottom-0 left-0 right-0 lg:pl-[272px] bg-surface-container/90 backdrop-blur-xl border-t border-outline-variant/30 p-4 z-40">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
          <div className="hidden sm:block">
            <p className="text-on-background font-lexend font-bold text-lg">System Ready</p>
            <p className="text-stone-600 font-manrope text-xs">Verify all telemetry before upload.</p>
          </div>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="flex-1 sm:flex-none w-full sm:w-64 bg-primary hover:bg-primary/90 text-on-primary py-4 px-8 rounded-xl font-lexend font-bold uppercase tracking-widest text-sm transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed shadow-[0_0_20px_rgba(30,122,70,0.2)] hover:shadow-[0_0_25px_rgba(30,122,70,0.4)] flex justify-center items-center gap-2"
          >
            {loading ? (
              <span className="material-symbols-outlined animate-spin">progress_activity</span>
            ) : (
              <span className="material-symbols-outlined">cloud_upload</span>
            )}
            {loading ? 'Transmitting...' : 'Upload Data'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddMatch;
